/* Site flags context — fetches /api/public/settings once and exposes feature flags
   (subscriptions_enabled, print_orders_enabled) globally so any component can hide
   pricing/order CTAs during soft-launch mode. */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const SiteFlagsContext = createContext({
  flags: { subscriptions_enabled: false, print_orders_enabled: false },
  settings: {},
  refresh: () => {},
  loading: true,
});

export function SiteFlagsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      let data;
      try {
        ({ data } = await api.get("/settings"));
      } catch {
        // One retry before failing closed (a transient failure would otherwise
        // bake flag-off content into crawler snapshots).
        await new Promise((r) => setTimeout(r, 800));
        ({ data } = await api.get("/settings"));
      }
      setSettings(data || {});
    } catch (e) {
      // Fail-closed: flags stay off. Log so dev URL-style bugs surface immediately.
      console.error("SiteFlagsContext: /settings fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const flags = {
    subscriptions_enabled: !!settings.subscriptions_enabled,
    print_orders_enabled: !!settings.print_orders_enabled,
    gamification_enabled: !!settings.gamification_enabled,
    recipe_creator_enabled: !!settings.recipe_creator_enabled,
    referrals_enabled: !!settings.referrals_enabled,
    historical_recipes_enabled: !!settings.historical_recipes_enabled,
    meal_planner_enabled: !!settings.meal_planner_enabled,
    // Nutrition cards default ON — the switch is only meaningful when admin
    // has explicitly set `nutrition_estimates_enabled: false`. Any other
    // value (missing, true, truthy) means show the card as usual.
    nutrition_estimates_enabled: settings.nutrition_estimates_enabled !== false,
    // Site-wide voice search / navigation FAB. Default ON — admin must
    // explicitly flip to false (via /admin/site-config) to disable.
    voice_search_enabled: settings.voice_search_enabled !== false,
    voice_nav_enabled: settings.voice_nav_enabled !== false,
    amazon_one_basket_enabled: !!settings.amazon_one_basket_enabled,
    voice_tour_enabled: !!settings.voice_tour_enabled,
  };

  const limits = {
    free_recipe_limit: Number(settings.free_recipe_limit ?? 5),
    free_cookbook_limit: Number(settings.free_cookbook_limit ?? 1),
  };

  return (
    <SiteFlagsContext.Provider value={{ flags, limits, settings, refresh, loading }}>
      {children}
    </SiteFlagsContext.Provider>
  );
}

export function useSiteFlags() {
  return useContext(SiteFlagsContext);
}
